import Model from '~/models/base/Model';

/**
 * class representing runbook
 * @extends Model
 * @author fm
 */
export default class Runbook extends Model {
    static _url = `${APP_URL.automation}/`;
    static _incidentUrl = `${Model._url}/incidents/`;

    constructor(id = null, options) {
        super(id, options);
    }

    static all(params) {
        const requestUrl = this._url;
        const requestPayload = { params: params ?? {} };
        return this.api.get(requestUrl, requestPayload);
    }

    static getActionMongoId() {
        const requestUrl = `${APP_URL.automation}/createActionMongoId/`;
        return this.api.get(requestUrl);
    }

    static getAvailableIntegrations() {
        const requestUrl = `${APP_URL.automation}/availableIntegrations/`;
        return this.api.get(requestUrl);
    }

    static isRunnable(runbookId) {
        const requestUrl = `${APP_URL.automation}/runbook/${runbookId}/is-runnable/`;
        return this.api.get(requestUrl);
    }

    static getRunbookGraph(incidentId, runbookId, notInCustomization, options = {}) {
        const requestUrl = `${APP_URL.automation}/runbookGraph/${incidentId}/${runbookId}/`;
        const baseParams = notInCustomization ? null : { playbook_customization: true };
        const { params: optionsParams, ...restOptions } = options;
        const mergedParams = { ...baseParams, ...optionsParams };
        const requestPayload = {
            ...(Object.keys(mergedParams).length > 0 && { params: mergedParams }),
            ...restOptions,
        };
        return this.api.get(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static wakeupRunbook(params) {
        const requestUrl = `${APP_URL.automation}/wakeupRunbook/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    static executeRunbook(params = {}) {
        const requestUrl = `${APP_URL.automation}/executeRunbook/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Gets nodes details regarding a playbook execution, including action results, to render graph view
     * @note If execution is not related to an incident, it will get all nodes details of all related executions
     * @param {integer} incidentId, will be 0 if related to a triage event or a Sumo object (e.g. CSE insight)
     * @param {string} runningId, the playbook execution ID
     * @returns {AxiosPromise<any>}
     */
    static getRunbookExecutionDetails(incidentId, runningId) {
        const requestUrl = `${APP_URL.automation}/runbookExecutionHistory/${incidentId}/${runningId}/`;
        const requestPayload = incidentId
            ? {}
            : { params: { getAllRelated: true } };
        return this.api.get(requestUrl, requestPayload);
    }

    static getRunbookHistory(incidentId, runbookId) {
        const requestUrl = `${APP_URL.automation}/runbookHistory/${incidentId}/${runbookId}/`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static killRunbook(params) {
        const requestUrl = `${APP_URL.automation}/killRunbook/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    static killSingleActionRunbook(params) {
        const requestUrl = `${APP_URL.automation}/killRunbookSingleAction/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static userchoiceAnswer(params) {
        const requestUrl = `${APP_URL.automation}/answerUserChoice/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Set a user choice answer
     * @param {string} runbookId
     * @param {string} runningId
     * @param {string} userChoiceId
     * @param {string} answer
     * @returns {AxiosPromise<any>}
     * @author mm
     */
    static answerUserChoice(runbookId, runningId, userChoiceId, answer) {
        const requestUrl = `${this._url}answerUserChoice/`;
        const requestPayload = {
            wf_id: runbookId,
            a_id: userChoiceId,
            running_id: runningId,
            answer: answer
        };
        return this.api.post(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static getSingleActionResult(incidentId, wfId, runningId, actionId) {
        const requestUrl = `${APP_URL.automation}/runbookActionResults/${incidentId}/${wfId}/${runningId}/${actionId}`;
        return this.api.get(requestUrl);
    }

    static getAvailableActions(integrationTag = '') {
        return new Promise(resolve => {
            this.api
                .get(
                    `${APP_URL.automation}/availableActions/${
                        integrationTag ? `?tag=${integrationTag}` : ''
                    }`
                )
                .then(response => {
                    const respData = response.data;
                    const actions = {};
                    [
                        'Enrichment',
                        'Containment',
                        'Custom',
                        'Notification',
                        'Scheduled'
                    ].forEach(type => {
                        actions[type] = respData[type]?.reduce(
                            (acc, current) => {
                                const currentNameSTR = current.name
                                    .trim()
                                    .toUpperCase();
                                let foundIndex = acc.findIndex(item => {
                                    return (
                                        item.internalName ===
                                            current.internalName &&
                                        currentNameSTR ===
                                            item.name.trim().toUpperCase()
                                    );
                                });
                                if (foundIndex < 0) {
                                    return acc.concat([current]);
                                } else {
                                    const found = acc[foundIndex];
                                    found.resources = [
                                        ...found.resources,
                                        ...current.resources
                                    ];
                                    return acc;
                                }
                            },
                            []
                        );
                    });
                    resolve({ data: actions });
                });
        });
    }

    static getActionUsers(params) {
        const requestUrl = `${APP_URL.automation}/actionUsers/`;
        const requestPayload = {
            params: params
        };
        return this.api.get(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static executeAction(action, incidentId, triageId, ...rest) {
        const requestUrl = `${this._url}executeObservableAction/`;
        let requestPayload = {
            op_type: 'execute_action',
            action_id: action.id,
            action_internal_name: action.internalName,
            asset_id: action.resourceId,
            configuration_values: JSON.stringify(action.configurationValues),
            triage_id: triageId ?? 0,
            incident_id: incidentId ?? 0
        };
        requestPayload = Object.assign({}, requestPayload, ...rest);
        return this.api.post(requestUrl, requestPayload);
    }

    static getActionResults(incidentId, action) {
        const requestUrl = `${this._url}automatic-actions/outputList/`;
        const requestPayload = {
            actions: action
        };
        return this.api.post(requestUrl, requestPayload);
    }

    static getRunbookActionResult(incidentId, runbookId, runningId, actionId) {
        const requestUrl = `${APP_URL.automation}/runbookActionResults/${incidentId}/${runbookId}/${runningId}/${actionId}/`;
        return this.api.get(requestUrl);
    }

    static downloadResults(incidentId, runningId, actionId) {
        const requestUrl = `${
            APP_URL.automation
        }/runbookResultsDownload/${incidentId}/${runningId}/${
            actionId ? `${actionId}/` : ''
        }`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static getRunbookSingleActionExecutionDetails(incidentId, runningId, aId) {
        const requestUrl = `${APP_URL.automation}/runbookSingleActionExecutionHistory/${incidentId}/${runningId}/${aId}/`;
        return this.api.get(requestUrl);
    }

    static saveSingleNodeRunbook(
        incidentId,
        triageId,
        wfId,
        runningId,
        aId,
        params
    ) {
        const requestUrl = `${APP_URL.automation}/runbookSingleActionUpdate/${wfId}/${runningId}/${aId}/`;
        const requestPayload = {
            ...params,
            incident_id: incidentId,
            triage_id: triageId
        };
        return this.api.post(requestUrl, requestPayload);
    }

    static saveRunbook(incidentId, runbookId, params) {
        const requestUrl = `${this._incidentUrl}${incidentId}/runbook/${runbookId}/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static getCustomizationList() {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/`;
        return this.api.get(requestUrl);
    }

    static getRunbookDetails(id) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/detail/`;
        return this.api.get(requestUrl);
    }

    static createRunbookFromCustomization(params) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    static saveRunbookFromCustomization(id, revisionId, params) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/${revisionId}/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static testRevision(params) {
        const requestUrl = `${APP_URL.automation}/testPlaybook/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    static deleteRevision(id, revisionId) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/${revisionId}/delete/`;
        return this.api.patch(requestUrl);
    }

    static restoreRevision(id, revisionId) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/${revisionId}/restore/`;
        return this.api.patch(requestUrl);
    }

    static updateRunbook(id, revisionId, data) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/${revisionId}/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = data;
        return this.api.patch(requestUrl, requestPayload);
    }

    static publishRevison(id, revisionId, params) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/${revisionId}/publish/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    static newDraft(id, revisionId) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/${revisionId}/newDraft/`;
        return this.api.post(requestUrl);
    }

    static getRunbookRevisions(id) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static deleteRunbook(id) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/delete/`;
        return this.api.patch(requestUrl);
    }

    static permanentlyDeleteRunbook(name) {
        const requestUrl = `${this._publicApiUrl}/v3/playbook/?name=${encodeURIComponent(name)}`;
        return this.api.delete(requestUrl);
    }

    static restoreRunbook(id) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/restore/`;
        return this.api.patch(requestUrl);
    }

    static enableRunbook(id) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/enable/`;
        return this.api.patch(requestUrl);
    }

    static disableRunbook(id) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/disable/`;
        return this.api.patch(requestUrl);
    }

    static duplicateRunbook(id, params) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/clone/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static exportRunbook(id) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/${id}/export/`;
        const requestPayload = { responseType: 'blob' };
        return this.api.get(requestUrl, requestPayload);
    }

    static importRunbook(params) {
        const requestUrl = `${APP_URL.automation}/customizations/playbook/import/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }
}
